import { ApolloQueryResult } from 'apollo-client';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../actions';
import { acceptMassActionsBannersPrice } from '../actions/banners-edit/banners-edit-mass-actions';
import {
    closeBannersPricesPopup,
    RESET_BANNERS_PRICES,
    ResetBannersPrices,
    SAVE_BANNERS_PRICES,
    SaveBannersPrices,
    saveBannersPrices,
    SwitchBannersPricesPopupForm,
    switchBannersPricesPopupForm,
    updateBannersPricesPopupErrors,
    UpdateBannersPricesPopupErrors,
    VALIDATE_BANNERS_PRICES,
    ValidateBannersPrices
} from '../actions/banners-prices';
import { resetGridData } from '../actions/grid-screen';
import { selectPriceEditableIds } from '../selectors/bannerEdit/massActions/massActions';
import { mapBannerPriceErrors } from '../service/BannerEditErrors/BannerEditErrors';
import { getBannerPriceDataToApi } from '../service/BannerHelpers/BannerHelpers';
import {
    getBannersPricesSavedResultMessage,
    showBannersPartialUpdateMessage
} from '../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { MassActionId } from '../typings/bannersEditState';
import { DnaEpicAction } from '../typings/epic';
import { updateAdPrices, validateAdPrice } from '../typings/graphql-grid';
import { State } from '../typings/state';

type ValidateBannersPricesEpicObservable = Observable<
    UpdateBannersPricesPopupErrors | SaveBannersPrices | SwitchBannersPricesPopupForm
>;

export const validateBannersPricesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(VALIDATE_BANNERS_PRICES).mergeMap(
        (action: ValidateBannersPrices): ValidateBannersPricesEpicObservable => {
            const state = store$.value;
            const { bannerId } = action;
            const bannerPrice = state.bannersPriceEditor[bannerId].bannerPrice;

            if (!bannerPrice.price) {
                return Observable.of(switchBannersPricesPopupForm(bannerId));
            }

            return Observable.fromPromise(deps.bannersDataProvider.validateBannerPrice([bannerPrice]))
                .mergeMap(
                    (result: ApolloQueryResult<validateAdPrice>): ValidateBannersPricesEpicObservable => {
                        if (result.data) {
                            if (result.data.validateAdPrice) {
                                return Observable.of(
                                    updateBannersPricesPopupErrors(
                                        mapBannerPriceErrors(result.data.validateAdPrice.errors),
                                        bannerId
                                    )
                                );
                            } else {
                                return Observable.of(saveBannersPrices(bannerId));
                            }
                        } else {
                            return Observable.of(
                                updateBannersPricesPopupErrors(
                                    mapBannerPriceErrors([
                                        {
                                            code: 'API_ERROR',
                                            params: null,
                                            path: ''
                                        }
                                    ]),
                                    bannerId
                                )
                            );
                        }
                    }
                )
                .catch(() => {
                    return Observable.of(
                        updateBannersPricesPopupErrors(
                            mapBannerPriceErrors([
                                {
                                    code: 'API_ERROR',
                                    params: null,
                                    path: ''
                                }
                            ]),
                            bannerId
                        )
                    );
                });
        }
    );
};

export const saveBannersPricesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_BANNERS_PRICES),
        mergeMap(
            (action: SaveBannersPrices): DnaEpicAction => {
                const state = store$.value;
                const { bannerId } = action;
                const bannerPrice = state.bannersPriceEditor[bannerId].bannerPrice;
                const selectedBanners = selectPriceEditableIds(state);
                const selectedBannersCount = selectedBanners.length;
                const price = getBannerPriceDataToApi(bannerPrice);
                const requestData = selectedBanners.map(id => ({
                    id: id,
                    price: price
                }));

                if (bannerId === MassActionId.bannersEdit) {
                    return of(acceptMassActionsBannersPrice(MassActionId.bannersEdit, bannerId));
                }

                return from(deps.bannersMutationExecutor.updateAdPricesMass(requestData)).pipe(
                    mergeMap(
                        (result: ApolloQueryResult<updateAdPrices>): DnaEpicAction => {
                            const updatedBanners = result.data.updateAdPrices.updatedAds;
                            const updatedBannersCount = updatedBanners.length;
                            const unsavedBannersCount = selectedBannersCount - updatedBannersCount;
                            const resultMessage = getBannersPricesSavedResultMessage(
                                updatedBannersCount,
                                unsavedBannersCount
                            );
                            const updatedBannerIds = updatedBanners.map(banner => (banner ? banner.id.toString() : ''));

                            showBannersPartialUpdateMessage(
                                resultMessage,
                                selectedBanners,
                                updatedBannerIds,
                                state.commonData.login,
                                'SHOW_BANNERS_WITH_UNSAVED_PRICE'
                            );

                            return [closeBannersPricesPopup(bannerId), resetGridData()];
                        }
                    ),
                    catchError(
                        (): Observable<UpdateBannersPricesPopupErrors> =>
                            of(
                                updateBannersPricesPopupErrors(
                                    mapBannerPriceErrors([
                                        {
                                            code: 'API_ERROR',
                                            params: null,
                                            path: ''
                                        }
                                    ]),
                                    bannerId
                                )
                            )
                    )
                );
            }
        )
    );
};

export const resetBannersPricesEpic: Epic<Action, Action, State, EpicDeps> = action$ => {
    return action$.ofType(RESET_BANNERS_PRICES).mergeMap((action: ResetBannersPrices) => {
        return Observable.of(saveBannersPrices(action.bannerId));
    });
};
